import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import GenericDialog from "@components/GenericDialog/GenericDialog";

type NavigationAction = () => void;

export type NavigationBlocker = {
  title: string;
  description: string;
  cancelButtonText: string;
  confirmButtonText: string;
  onConfirm?: NavigationAction;
};

type PendingNavigation = {
  action: NavigationAction;
  onCancel?: NavigationAction;
  blocker: NavigationBlocker;
  onConfirm: NavigationAction[];
};

interface NavigationGuardContextValue {
  requestNavigation: (action: NavigationAction, onCancel?: NavigationAction) => boolean;
  setNavigationBlocker: (source: string, blocker: NavigationBlocker | null) => void;
  hasActiveNavigationBlockers: boolean;
}

const defaultNavigationGuard: NavigationGuardContextValue = {
  requestNavigation: (action) => {
    action();
    return true;
  },
  setNavigationBlocker: () => {},
  hasActiveNavigationBlockers: false,
};

const NavigationGuardContext = createContext<NavigationGuardContextValue>(defaultNavigationGuard);

export const NavigationGuardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [navigationBlockers, setNavigationBlockers] = useState<Map<string, NavigationBlocker>>(
    new Map(),
  );
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);

  const setNavigationBlocker = useCallback((source: string, blocker: NavigationBlocker | null) => {
    setNavigationBlockers((current) => {
      const next = new Map(current);

      if (blocker) {
        next.set(source, blocker);
      } else {
        next.delete(source);
      }

      return next;
    });
  }, []);

  const requestNavigation = useCallback(
    (action: NavigationAction, onCancel?: NavigationAction) => {
      const activeBlockers = Array.from(navigationBlockers.values());

      if (activeBlockers.length === 0) {
        action();
        return true;
      }

      setPendingNavigation({
        action,
        onCancel,
        blocker: activeBlockers[0],
        onConfirm: activeBlockers
          .map((blocker) => blocker.onConfirm)
          .filter((onConfirm): onConfirm is NavigationAction => Boolean(onConfirm)),
      });
      return false;
    },
    [navigationBlockers],
  );

  const cancelNavigation = () => {
    pendingNavigation?.onCancel?.();
    setPendingNavigation(null);
  };

  const confirmNavigation = () => {
    const pending = pendingNavigation;
    setPendingNavigation(null);
    pending?.onConfirm.forEach((onConfirm) => onConfirm());
    pending?.action();
  };

  const value = useMemo(
    () => ({
      requestNavigation,
      setNavigationBlocker,
      hasActiveNavigationBlockers: navigationBlockers.size > 0,
    }),
    [navigationBlockers.size, requestNavigation, setNavigationBlocker],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
      <GenericDialog
        open={Boolean(pendingNavigation)}
        title={pendingNavigation?.blocker.title ?? ""}
        content={pendingNavigation?.blocker.description ?? ""}
        cancelButtonText={pendingNavigation?.blocker.cancelButtonText ?? ""}
        confirmButtonText={pendingNavigation?.blocker.confirmButtonText ?? ""}
        handleCloseDialog={cancelNavigation}
        confirmAction={confirmNavigation}
        testId="navigation-guard-dialog"
        zIndex={10000}
      />
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuard = () => useContext(NavigationGuardContext);
